/**
 * @fileoverview 人材管理（タレントマネジメント）
 * @description SmartHR・CYDAS等のタレントマネジメントを参考にした乗組員の人材データベース。
 *              「名簿 / 人員分析 / 免状・手帳期限」の3タブ構成。
 *              - 名簿: カード/リスト、船別・免状別・役職別グルーピング、KPIクリック絞込、
 *                      陸上日数・乗船日数・下船予定など配乗計画に効く指標を表示
 *              - 人員分析: 年齢構成・免状構成・通算乗船分布などのチャート（クリックで名簿へドリルダウン）
 *              - 免状・手帳期限: 期限切れ・失効間近のワークリスト（期間フィルタ＋Excel出力）
 *              個人詳細は共通ドロワー（TalentDetailDrawer）を使用。
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Download, LayoutGrid, LayoutList, Ship, Anchor, Users, Award,
  AlertTriangle, Calendar, History, NotebookPen, IdCard, BarChart2, ChevronDown,
  Filter, Trash2, Save,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import * as api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { hasFeature } from '../utils/featureFlags';
import AvatarEditor from '../components/AvatarEditor';
import { usePager } from '../components/TableTools';
import { exportSimpleList } from '../utils/excelHelper';
import TalentDetailDrawer, {
  Pill, StatusBadge, ExpiryPill,
  fmtDate, calcAge, fmtDuration, expiryInfo, daysUntil,
  setTalentThresholds, getLongBoardingDays, getLicenseWarnDays,
  type TalentProfile, type VesselSummary,
} from '../components/TalentDetailDrawer';

// ── 型定義（名簿行 = プロフィール + 履歴サマリ） ─────────────────────────
interface TalentRow extends TalentProfile {
  current: NonNullable<TalentProfile['current']> | null;
  scheduled: NonNullable<TalentProfile['scheduled']> | null;
  history: { boarding_count: number; total_days: number; vessel_count: number; vessels: VesselSummary[] };
  external: { count: number; total_days: number };
  last_departed_at: string | null;
  days_ashore: number | null;   // 直近下船からの経過日数（乗船中は null）
}
interface Vessel { id: string; vessel_name: string }

// ── 区分ヘルパー（分析タブのドリルダウン軸） ─────────────────────────────
const AGE_BANDS = ['29歳以下', '30代', '40代', '50代', '60歳以上', '未登録'] as const;
function ageBandOf(age: number | null): string {
  if (age == null) return '未登録';
  if (age <= 29) return '29歳以下';
  if (age <= 39) return '30代';
  if (age <= 49) return '40代';
  if (age <= 59) return '50代';
  return '60歳以上';
}
const DUR_BANDS = ['1年未満', '1〜3年', '3〜5年', '5〜10年', '10年以上'] as const;
function durBandOf(days: number): string {
  if (days < 365) return '1年未満';
  if (days < 365 * 3) return '1〜3年';
  if (days < 365 * 5) return '3〜5年';
  if (days < 365 * 10) return '5〜10年';
  return '10年以上';
}
const totalDaysOf = (r: TalentRow) => r.history.total_days + r.external.total_days;

// ── KPI カード ────────────────────────────────────────────────────────────
const StatCard: React.FC<{
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string;
  color: string; bg: string; active?: boolean; onClick?: () => void;
}> = ({ icon, label, value, sub, color, bg, active, onClick }) => (
  <div className="stat-card" onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 14, cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid var(--border)',
      boxShadow: active ? `0 4px 14px ${color}33` : undefined,
      transition: 'box-shadow .12s, border-color .12s, transform .12s',
    }}
    onMouseEnter={e => { if (onClick && !active) e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
    title={onClick ? 'クリックで絞り込み' : undefined}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{label}{sub && <span style={{ color: '#94a3b8', fontWeight: 500 }}> ・{sub}</span>}</div>
    </div>
  </div>
);

/** 分析タブのチャートカード */
const ChartCard: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="card" style={{ margin: 0 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{title}</span>
      {hint && <span style={{ fontSize: 11, color: '#94a3b8' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

/** 乗船中/陸上の経過日数ピル（配乗計画の判断材料） */
const DaysPill: React.FC<{ row: TalentRow }> = ({ row }) => {
  if (row.current) {
    const bd = row.current.boarding_days;
    const departIn = daysUntil(row.current.planned_departure_at ?? null);
    const long = (bd ?? 0) >= getLongBoardingDays();
    return (
      <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
        {bd != null && (
          <Pill bg={long ? '#fff7ed' : '#f0fdf4'} fg={long ? '#c2410c' : '#15803d'} border={long ? '#fed7aa' : '#bbf7d0'}
            title={long ? `長期乗船中（${getLongBoardingDays()}日以上）` : undefined}>
            乗船{bd}日目{long ? ' ⚠' : ''}
          </Pill>
        )}
        {departIn != null && departIn >= 0 && (
          <Pill bg={departIn <= 14 ? '#fffbeb' : '#f8fafc'} fg={departIn <= 14 ? '#b45309' : '#64748b'} border={departIn <= 14 ? '#fde68a' : '#e2e8f0'}
            title={`下船予定 ${fmtDate(row.current.planned_departure_at)}`}>
            あと{departIn}日で下船
          </Pill>
        )}
      </span>
    );
  }
  if (row.days_ashore != null) {
    const long = row.days_ashore >= 30;
    return (
      <Pill bg="#eff6ff" fg="#1d4ed8" border="#bfdbfe" title={long ? '陸上30日以上 — 次の配乗候補' : `直近下船: ${fmtDate(row.last_departed_at)}`}>
        陸上{row.days_ashore}日{long ? ' ✓' : ''}
      </Pill>
    );
  }
  return <span style={{ fontSize: 11.5, color: '#cbd5e1' }}>-</span>;
};

// ── 名簿カード（カード表示の1人分） ──────────────────────────────────────
const PersonCard: React.FC<{ row: TalentRow; onClick: () => void }> = ({ row, onClick }) => {
  const total = totalDaysOf(row);
  const licInfo = expiryInfo(row.license_expiry_date);
  const age = calcAge(row.birth_date);
  const borderColor = row.current ? '#22c55e' : row.scheduled ? '#3b82f6' : '#cbd5e1';
  return (
    <div onClick={onClick}
      style={{
        background: '#fff', border: '1px solid #e5e7eb', borderLeft: `4px solid ${borderColor}`,
        borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
        transition: 'box-shadow .15s, transform .15s', display: 'flex', flexDirection: 'column', gap: 9,
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(15,23,42,0.10)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div onClick={e => e.stopPropagation()}>
          <AvatarEditor entityId={row.id} type="user" imagePath={row.avatar_path} displayName={row.user_name} size={48} readOnly onUpdated={() => {}} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span style={{ fontSize: 14.5, fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.user_name}</span>
            {row.name_kana && <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name_kana}</span>}
          </div>
          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
            {row.employee_code}{age != null && <span> ・ {age}歳</span>}
          </div>
        </div>
        <StatusBadge row={row} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minHeight: 22 }}>
        {row.current
          ? <Pill bg="#ecfeff" fg="#0e7490" border="#a5f3fc"><Ship size={11} />{row.current.vessel_name}{row.current.position ? ` / ${row.current.position}` : ''}</Pill>
          : row.scheduled
            ? <Pill bg="#eff6ff" fg="#2563eb" border="#bfdbfe"><Calendar size={11} />{row.scheduled.vessel_name} 予定</Pill>
            : <span style={{ fontSize: 11.5, color: '#94a3b8' }}>未配乗</span>}
        {row.license_grade
          ? <Pill bg="#fdf4ff" fg="#a21caf" border="#f5d0fe" title={row.license_expiry_date ? `期限: ${fmtDate(row.license_expiry_date)}` : ''}>
              <Award size={11} />{row.license_grade}
              {(licInfo.level === 'expired' || licInfo.level === 'soon') && <AlertTriangle size={11} color={licInfo.level === 'expired' ? '#dc2626' : '#d97706'} />}
            </Pill>
          : <Pill bg="#f8fafc" fg="#94a3b8" border="#e2e8f0">免状未登録</Pill>}
        <DaysPill row={row} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11.5, color: '#475569', borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
        <span title="経験船舶数"><Ship size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{row.history.vessel_count}隻</span>
        <span title="乗船回数（手動入力含む）"><History size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{row.history.boarding_count + row.external.count}回</span>
        <span title={`通算 ${total.toLocaleString()}日（手動入力含む）`} style={{ fontWeight: 700, color: '#0369a1' }}>通算 {fmtDuration(total)}</span>
        {row.crew_note && (
          <span title={row.crew_note} style={{ marginLeft: 'auto', color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
            <AlertTriangle size={11} style={{ flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{row.crew_note}</span>
          </span>
        )}
      </div>
    </div>
  );
};

// ── メインページ ──────────────────────────────────────────────────────────
type ViewMode = 'card' | 'list';
type GroupMode = 'none' | 'vessel' | 'license' | 'position';
type PageTab = 'roster' | 'analytics' | 'expiry';

const CHART_BLUE = '#0ea5e9';
const CHART_DEEP = '#0369a1';

const TalentPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<TalentRow[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [licenseGrades, setLicenseGrades] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageTab, setPageTab] = useState<PageTab>('roster');
  const [q, setQ] = useState('');
  const [vesselFilter, setVesselFilter] = useState('');
  const [licenseFilter, setLicenseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ageBand, setAgeBand] = useState('');
  const [durBand, setDurBand] = useState('');
  const [groupMode, setGroupMode] = useState<GroupMode>('vessel');
  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('talent_view') as ViewMode) || 'card');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<'name' | 'total' | 'code' | 'ashore' | 'boarding'>('name');
  const [expiryDays, setExpiryDays] = useState(90);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // 保存ビュー（管理者間で共有する名前付きフィルタ）
  interface SavedFilter { id: string; name: string; payload: Record<string, any>; created_by_name: string | null }
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showViewMenu, setShowViewMenu] = useState(false);

  const loadSavedFilters = useCallback(async () => {
    try {
      const res = await api.get<any>('/talent/saved-filters');
      setSavedFilters((res.data ?? []) as SavedFilter[]);
    } catch { /* ignore */ }
  }, []);

  const applySavedFilter = (f: SavedFilter) => {
    const p = f.payload ?? {};
    setQ(String(p.q ?? ''));
    setVesselFilter(String(p.vesselFilter ?? ''));
    setLicenseFilter(String(p.licenseFilter ?? ''));
    setStatusFilter(String(p.statusFilter ?? ''));
    setAgeBand(String(p.ageBand ?? ''));
    setDurBand(String(p.durBand ?? ''));
    if (p.sortKey) setSortKey(p.sortKey);
    if (p.groupMode) setGroupMode(p.groupMode);
    setShowViewMenu(false);
  };

  const saveCurrentFilter = async () => {
    const name = window.prompt('この絞り込み条件の名前を入力してください（管理者間で共有されます）', '');
    if (!name?.trim()) return;
    try {
      await api.post('/talent/saved-filters', {
        name: name.trim(),
        payload: { q, vesselFilter, licenseFilter, statusFilter, ageBand, durBand, sortKey, groupMode },
      });
      await loadSavedFilters();
    } catch (e: any) { alert(e?.message ?? '保存に失敗しました'); }
  };

  const deleteSavedFilter = async (f: SavedFilter) => {
    if (!window.confirm(`保存ビュー「${f.name}」を削除しますか？（全管理者から見えなくなります）`)) return;
    try {
      await api.del(`/talent/saved-filters/${f.id}`);
      await loadSavedFilters();
    } catch { alert('削除に失敗しました'); }
  };

  // 会社別の警告しきい値（表示ラベル用。判定ロジックは setTalentThresholds 経由）
  const [warnDays, setWarnDays] = useState({ license: getLicenseWarnDays(), boarding: getLongBoardingDays() });

  const loadRoster = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get<any>('/talent/roster');
      setTalentThresholds(res.thresholds);
      setWarnDays({ license: getLicenseWarnDays(), boarding: getLongBoardingDays() });
      setRows((res.data ?? []) as TalentRow[]);
    } catch (e) { console.error('[Talent/roster]', e); }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    loadRoster();
    (async () => {
      try {
        const v = await api.get<any>('/vessels');
        setVessels(((v.vessels ?? v.data ?? []) as Vessel[]));
      } catch { /* ignore */ }
      try {
        const g = await api.get<any>('/license-grade-masters');
        const names = ((g.data ?? []) as Array<{ name: string }>).map(x => x.name);
        setLicenseGrades(names);
      } catch { /* マスタ未整備の環境ではデータから抽出 */ }
      loadSavedFilters();
    })();
  }, [loadRoster, loadSavedFilters]);

  const changeView = (m: ViewMode) => { setViewMode(m); localStorage.setItem('talent_view', m); };

  // フィルタリング
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = rows;
    if (query) {
      list = list.filter(r =>
        r.user_name.toLowerCase().includes(query) ||
        (r.name_kana ?? '').toLowerCase().includes(query) ||
        r.employee_code.toLowerCase().includes(query));
    }
    // 船舶フィルタ: 配属中 (vesselId) / 乗船経験 (exp:vesselId) / 未配乗 (none)
    if (vesselFilter === 'none') list = list.filter(r => !r.current);
    else if (vesselFilter.startsWith('exp:')) {
      const vid = vesselFilter.slice(4);
      list = list.filter(r => r.history.vessels.some(v => v.vessel_id === vid));
    }
    else if (vesselFilter) list = list.filter(r => r.current?.vessel_id === vesselFilter);
    if (licenseFilter === 'none') list = list.filter(r => !r.license_grade);
    else if (licenseFilter) list = list.filter(r => r.license_grade === licenseFilter);
    if (statusFilter === 'on_board') list = list.filter(r => !!r.current);
    else if (statusFilter === 'scheduled') list = list.filter(r => !r.current && !!r.scheduled);
    else if (statusFilter === 'ashore') list = list.filter(r => !r.current && !r.scheduled);
    else if (statusFilter === 'expiry') list = list.filter(r => ['expired', 'soon'].includes(expiryInfo(r.license_expiry_date).level) || ['expired', 'soon'].includes(expiryInfo(r.seaman_book_expiry_date).level));
    else if (statusFilter === 'note') list = list.filter(r => !!r.crew_note);
    else if (statusFilter === 'long_boarding') list = list.filter(r => (r.current?.boarding_days ?? 0) >= getLongBoardingDays());
    else if (statusFilter === 'departing_soon') {
      list = list.filter(r => {
        const d = daysUntil(r.current?.planned_departure_at ?? null);
        return d != null && d >= 0 && d <= 14;
      });
    }
    if (ageBand) list = list.filter(r => ageBandOf(calcAge(r.birth_date)) === ageBand);
    if (durBand) list = list.filter(r => durBandOf(totalDaysOf(r)) === durBand);
    // ソート
    const sorted = [...list];
    if (sortKey === 'name') sorted.sort((a, b) => (a.name_kana ?? a.user_name).localeCompare(b.name_kana ?? b.user_name, 'ja'));
    else if (sortKey === 'code') sorted.sort((a, b) => a.employee_code.localeCompare(b.employee_code, 'ja'));
    else if (sortKey === 'ashore') sorted.sort((a, b) => (b.days_ashore ?? -1) - (a.days_ashore ?? -1));
    else if (sortKey === 'boarding') sorted.sort((a, b) => (b.current?.boarding_days ?? -1) - (a.current?.boarding_days ?? -1));
    else sorted.sort((a, b) => totalDaysOf(b) - totalDaysOf(a));
    return sorted;
  }, [rows, q, vesselFilter, licenseFilter, statusFilter, ageBand, durBand, sortKey]);

  // グルーピング
  const groups = useMemo(() => {
    if (groupMode === 'none') return [{ key: '_all', label: '', rows: filtered }];
    const map = new Map<string, TalentRow[]>();
    const keyOf = (r: TalentRow): string => {
      if (groupMode === 'vessel') return r.current?.vessel_name || (r.scheduled ? `（乗船予定）${r.scheduled.vessel_name}` : '未配乗（陸上・休暇）');
      if (groupMode === 'license') return r.license_grade || '免状未登録';
      return r.current?.position || (r.current ? '役職未設定' : '未配乗');
    };
    for (const r of filtered) {
      const k = keyOf(r);
      map.set(k, [...(map.get(k) ?? []), r]);
    }
    const LAST_KEYS = ['未配乗（陸上・休暇）', '免状未登録', '未配乗', '役職未設定'];
    return [...map.entries()]
      .sort((a, b) => {
        const ai = LAST_KEYS.indexOf(a[0]); const bi = LAST_KEYS.indexOf(b[0]);
        if (ai !== -1 || bi !== -1) return (ai === -1 ? -1 : bi === -1 ? 1 : ai - bi);
        return a[0].localeCompare(b[0], 'ja');
      })
      .map(([label, groupRows]) => ({ key: label, label, rows: groupRows }));
  }, [filtered, groupMode]);

  const pager = usePager(filtered, { defaultPageSize: 50, sizes: [20, 50, 100], noun: '名' });
  const usePaging = groupMode === 'none';
  const displayGroups = usePaging ? [{ key: '_all', label: '', rows: pager.pageRows }] : groups;

  // KPI（名簿タブ）
  const kpi = useMemo(() => {
    const onBoard = rows.filter(r => !!r.current).length;
    // 状態フィルタと同じ定義に揃える（乗船中 / 乗船予定 / 陸上・休暇 は排他）
    const scheduled = rows.filter(r => !r.current && !!r.scheduled).length;
    const ashore = rows.filter(r => !r.current && !r.scheduled).length;
    const expiry = rows.filter(r =>
      ['expired', 'soon'].includes(expiryInfo(r.license_expiry_date).level) ||
      ['expired', 'soon'].includes(expiryInfo(r.seaman_book_expiry_date).level)).length;
    const noted = rows.filter(r => !!r.crew_note).length;
    return { total: rows.length, onBoard, scheduled, ashore, expiry, noted };
  }, [rows]);

  const hasAnyFilter = !!(statusFilter || vesselFilter || licenseFilter || ageBand || durBand || q);
  const clearFilters = () => { setStatusFilter(''); setVesselFilter(''); setLicenseFilter(''); setAgeBand(''); setDurBand(''); setQ(''); };
  // KPIカードのクリック → 状態フィルタをトグル（登録乗組員は全解除）
  const clickKpi = (filter: string) => {
    if (filter === '') { clearFilters(); return; }
    setStatusFilter(prev => (prev === filter ? '' : filter));
  };

  // ── 人員分析（分析タブ） ──────────────────────────────────────────────
  const analytics = useMemo(() => {
    const ages = rows.map(r => calcAge(r.birth_date)).filter((a): a is number => a != null);
    const avgAge = ages.length ? Math.round(ages.reduce((s, a) => s + a, 0) / ages.length * 10) / 10 : null;
    const totals = rows.map(totalDaysOf);
    const avgTotal = totals.length ? Math.round(totals.reduce((s, d) => s + d, 0) / totals.length) : 0;
    const licenseHolders = rows.filter(r => !!r.license_grade).length;

    const ageDist = AGE_BANDS.map(b => ({ name: b, count: rows.filter(r => ageBandOf(calcAge(r.birth_date)) === b).length }))
      .filter(d => !(d.name === '未登録' && d.count === 0));
    const licMap = new Map<string, number>();
    rows.forEach(r => { const k = r.license_grade || '免状未登録'; licMap.set(k, (licMap.get(k) ?? 0) + 1); });
    const licDist = [...licMap.entries()].map(([name, count]) => ({ name, count }))
      .sort((a, b) => (a.name === '免状未登録' ? 1 : b.name === '免状未登録' ? -1 : b.count - a.count));
    const durDist = DUR_BANDS.map(b => ({ name: b, count: rows.filter(r => durBandOf(totalDaysOf(r)) === b).length }));
    const vesMap = new Map<string, { id: string; count: number }>();
    rows.forEach(r => {
      if (!r.current) return;
      const cur = vesMap.get(r.current.vessel_name) ?? { id: r.current.vessel_id, count: 0 };
      cur.count += 1;
      vesMap.set(r.current.vessel_name, cur);
    });
    const vesselDist = [...vesMap.entries()].map(([name, v]) => ({ name, id: v.id, count: v.count }))
      .sort((a, b) => b.count - a.count);
    const ashoreDays = rows.filter(r => r.days_ashore != null).map(r => r.days_ashore!) ;
    const avgAshore = ashoreDays.length ? Math.round(ashoreDays.reduce((s, d) => s + d, 0) / ashoreDays.length) : null;
    return { avgAge, avgTotal, licenseHolders, ageDist, licDist, durDist, vesselDist, avgAshore };
  }, [rows]);

  // ── 免状・手帳 期限ワークリスト（期限タブ） ──────────────────────────
  const expiryList = useMemo(() => {
    interface ExpiryRow { user: TalentRow; kind: '海技免状' | '船員手帳'; grade: string; date: string; days: number }
    const list: ExpiryRow[] = [];
    for (const r of rows) {
      for (const [kind, date, grade] of [
        ['海技免状', r.license_expiry_date, r.license_grade ?? '-'],
        ['船員手帳', r.seaman_book_expiry_date, r.seaman_book_no ?? '-'],
      ] as ['海技免状' | '船員手帳', string | null, string][]) {
        if (!date) continue;
        const info = expiryInfo(date);
        if (info.level === 'expired' || (info.level !== 'none' && info.days <= expiryDays)) {
          list.push({ user: r, kind, grade, date, days: info.days });
        }
      }
    }
    return list.sort((a, b) => a.days - b.days);
  }, [rows, expiryDays]);

  // 免状フィルタの選択肢（マスタ＋実データの和集合）
  const licenseOptions = useMemo(() => {
    const set = new Set<string>(licenseGrades);
    rows.forEach(r => { if (r.license_grade) set.add(r.license_grade); });
    return [...set].sort((a, b) => a.localeCompare(b, 'ja'));
  }, [rows, licenseGrades]);

  // ── Excel出力（フォーマット選択式・SmartHRのカスタムDL方式） ─────────
  const ymd = () => {
    const t = new Date();
    return `${t.getFullYear()}${String(t.getMonth() + 1).padStart(2, '0')}${String(t.getDate()).padStart(2, '0')}`;
  };

  const exportRoster = async () => {
    const header = [
      { label: '社員コード', width: 12 }, { label: '氏名', width: 16 }, { label: 'フリガナ', width: 16 },
      { label: '年齢', width: 7 }, { label: '状態', width: 10 }, { label: '配属船舶', width: 16 }, { label: '役職', width: 12 },
      { label: '乗船/陸上日数', width: 13 }, { label: '下船予定', width: 12 },
      { label: '免状種別', width: 16 }, { label: '免状番号', width: 14 }, { label: '免状期限', width: 12 },
      { label: '手帳番号', width: 14 }, { label: '手帳期限', width: 12 }, { label: '生年月日', width: 12 }, { label: '住所', width: 34 },
      { label: '通算乗船日数', width: 13 }, { label: '乗船回数', width: 10 }, { label: '経験船舶数', width: 11 },
      { label: '配乗上の注意', width: 34 },
    ];
    const body = filtered.map(r => [
      r.employee_code, r.user_name, r.name_kana ?? '',
      calcAge(r.birth_date) ?? '',
      r.current ? '乗船中' : r.scheduled ? '乗船予定' : '陸上',
      r.current?.vessel_name ?? r.scheduled?.vessel_name ?? '',
      r.current?.position ?? '',
      r.current ? (r.current.boarding_days != null ? `乗船${r.current.boarding_days}日目` : '') : (r.days_ashore != null ? `陸上${r.days_ashore}日` : ''),
      r.current?.planned_departure_at ? fmtDate(r.current.planned_departure_at) : '',
      r.license_grade ?? '', r.license_no ?? '',
      r.license_expiry_date ? fmtDate(r.license_expiry_date) : '',
      r.seaman_book_no ?? '',
      r.seaman_book_expiry_date ? fmtDate(r.seaman_book_expiry_date) : '',
      r.birth_date ? fmtDate(r.birth_date) : '',
      r.address ?? '',
      totalDaysOf(r),
      r.history.boarding_count + r.external.count,
      r.history.vessel_count,
      r.crew_note ?? '',
    ]);
    await exportSimpleList(header, body, '乗組員名簿', `乗組員名簿_${ymd()}.xlsx`, `乗組員名簿（人材管理） ${fmtDate(new Date().toISOString())} 現在`);
  };

  const exportExpiry = async () => {
    const header = [
      { label: '社員コード', width: 12 }, { label: '氏名', width: 16 }, { label: '区分', width: 10 },
      { label: '免状種別/手帳番号', width: 18 }, { label: '有効期限', width: 12 }, { label: '残日数', width: 9 },
      { label: '状態', width: 10 }, { label: '配属船舶', width: 16 }, { label: 'メール', width: 24 },
    ];
    const body = expiryList.map(e => [
      e.user.employee_code, e.user.user_name, e.kind, e.grade,
      fmtDate(e.date), e.days, e.days < 0 ? '期限切れ' : `${e.days}日以内`,
      e.user.current?.vessel_name ?? '陸上', e.user.email ?? '',
    ]);
    await exportSimpleList(header, body, '期限リスト', `免状手帳期限リスト_${ymd()}.xlsx`, `免状・船員手帳 期限リスト（${expiryDays}日以内＋期限切れ） ${fmtDate(new Date().toISOString())} 現在`);
  };

  const exportHistory = async () => {
    const header = [
      { label: '社員コード', width: 12 }, { label: '氏名', width: 16 }, { label: '船名', width: 18 },
      { label: '乗船回数', width: 10 }, { label: '通算日数', width: 11 }, { label: '初回乗船', width: 12 },
      { label: '直近下船', width: 12 }, { label: '乗船中', width: 8 }, { label: '経験役職', width: 20 },
    ];
    const body: (string | number)[][] = [];
    for (const r of filtered) {
      for (const v of r.history.vessels) {
        body.push([
          r.employee_code, r.user_name, v.vessel_name, v.times, v.total_days,
          v.first_boarded_at ? fmtDate(v.first_boarded_at) : '',
          v.last_at ? fmtDate(v.last_at) : '', v.on_board ? '○' : '', v.positions.join('、'),
        ]);
      }
    }
    await exportSimpleList(header, body, '乗船履歴サマリ', `乗船履歴サマリ_${ymd()}.xlsx`, `乗船履歴サマリ（船別集約） ${fmtDate(new Date().toISOString())} 現在`);
  };

  const selectStyle: React.CSSProperties = { padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', color: '#334155' };

  // 分析チャート → 名簿へのドリルダウン
  const drillTo = (apply: () => void) => { clearFilters(); apply(); setPageTab('roster'); };

  // リスト表示のテーブル
  const renderTable = (list: TalentRow[]) => (
    <div className="card" style={{ padding: 0, overflow: 'auto', marginBottom: 12 }}>
      <table>
        <thead><tr>
          <th>氏名</th><th>社員コード</th><th>年齢</th><th>状態</th><th>配属船舶 / 役職</th>
          <th>乗船/陸上</th><th>免状種別</th><th>免状期限</th><th style={{ textAlign: 'right' }}>通算乗船</th><th>配乗上の注意</th>
        </tr></thead>
        <tbody>
          {list.map(r => {
            const total = totalDaysOf(r);
            const age = calcAge(r.birth_date);
            return (
              <tr key={r.id} onClick={() => setSelectedId(r.id)} style={{ cursor: 'pointer' }}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div onClick={e => e.stopPropagation()}>
                      <AvatarEditor entityId={r.id} type="user" imagePath={r.avatar_path} displayName={r.user_name} size={34} readOnly onUpdated={() => {}} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.user_name}</div>
                      {r.name_kana && <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{r.name_kana}</div>}
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12.5, color: '#64748b' }}>{r.employee_code}</td>
                <td style={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' }}>{age != null ? `${age}歳` : '-'}</td>
                <td><StatusBadge row={r} /></td>
                <td style={{ fontSize: 12.5 }}>
                  {r.current
                    ? <>{r.current.vessel_name}{r.current.position && <span style={{ color: '#64748b' }}> / {r.current.position}</span>}</>
                    : r.scheduled ? <span style={{ color: '#2563eb' }}>{r.scheduled.vessel_name}（予定）</span>
                    : <span style={{ color: '#94a3b8' }}>-</span>}
                </td>
                <td><DaysPill row={r} /></td>
                <td style={{ fontSize: 12.5 }}>{r.license_grade || <span style={{ color: '#94a3b8' }}>-</span>}</td>
                <td><ExpiryPill date={r.license_expiry_date} /></td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1', whiteSpace: 'nowrap' }} title={`${total.toLocaleString()}日`}>
                  {fmtDuration(total)}
                  <span style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 500 }}> ({r.history.boarding_count + r.external.count}回)</span>
                </td>
                <td style={{ maxWidth: 200 }}>
                  {r.crew_note
                    ? <span style={{ fontSize: 12, color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
                        <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.crew_note}</span>
                      </span>
                    : <span style={{ color: '#cbd5e1' }}>-</span>}
                </td>
              </tr>
            );
          })}
          {list.length === 0 && <tr><td colSpan={10} className="empty">該当する乗組員がいません</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const chartTooltip = (
    <Tooltip
      contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      formatter={(v: number) => [`${v}名`, '人数']}
      labelStyle={{ fontWeight: 700, color: '#111827' }}
      cursor={{ fill: '#F4F6F8' }}
    />
  );

  return (
    <div className="page-wrap">
      {/* ヘッダー */}
      <div className="top-bar">
        <div>
          <h1 className="page-title">人材管理</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            乗組員のプロフィール・免状・乗船履歴を一元管理（タレントマネジメント）
          </p>
        </div>
        {/* Excel出力（フォーマット選択） */}
        <div style={{ position: 'relative' }}>
          <button className="btn-o" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setShowExportMenu(v => !v)}>
            <Download size={14} />Excel出力 <ChevronDown size={13} />
          </button>
          {showExportMenu && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 250, overflow: 'hidden' }}
              onMouseLeave={() => setShowExportMenu(false)}>
              {[
                { label: '📋 乗組員名簿（全項目）', desc: '現在の絞込条件を反映', fn: exportRoster },
                { label: '⚠️ 免状・手帳 期限リスト', desc: `${expiryDays}日以内＋期限切れ`, fn: exportExpiry },
                { label: '🚢 乗船履歴サマリ（船別）', desc: '乗組員×船舶の通算実績', fn: exportHistory },
              ].map(item => (
                <button key={item.label} onClick={() => { item.fn().catch(console.error); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{item.desc}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ページタブ */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1.5px solid #e2e8f0', marginBottom: 18 }}>
        {([
          ['roster', <Users key="i" size={15} />, '名簿'],
          ['analytics', <BarChart2 key="i" size={15} />, '人員分析'],
          ['expiry', <AlertTriangle key="i" size={15} />, `免状・手帳期限${expiryList.length > 0 ? `（${expiryList.length}）` : ''}`],
        ] as [PageTab, React.ReactNode, string][]).map(([key, icon, label]) => (
          <button key={key} onClick={() => setPageTab(key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '10px 20px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
              border: 'none', background: 'transparent',
              color: pageTab === key ? '#0369a1' : '#64748b',
              borderBottom: pageTab === key ? '2.5px solid #0369a1' : '2.5px solid transparent',
              marginBottom: -1.5,
            }}>
            {icon}{label}
          </button>
        ))}
      </div>

      {/* ═══ タブ1: 名簿 ═══ */}
      {pageTab === 'roster' && (
        <>
          {/* KPI（クリックで絞り込み） */}
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <StatCard icon={<Users size={20} />} label="登録乗組員" value={`${kpi.total}名`} sub="クリックで全解除" color="#0369a1" bg="#e0f2fe"
              active={!hasAnyFilter} onClick={() => clickKpi('')} />
            <StatCard icon={<Ship size={20} />} label="乗船中" value={`${kpi.onBoard}名`} color="#16a34a" bg="#dcfce7"
              active={statusFilter === 'on_board'} onClick={() => clickKpi('on_board')} />
            <StatCard icon={<Calendar size={20} />} label="乗船予定" value={`${kpi.scheduled}名`} color="#2563eb" bg="#eff6ff"
              active={statusFilter === 'scheduled'} onClick={() => clickKpi('scheduled')} />
            <StatCard icon={<Anchor size={20} />} label="陸上・休暇" value={`${kpi.ashore}名`} color="#64748b" bg="#f1f5f9"
              active={statusFilter === 'ashore'} onClick={() => clickKpi('ashore')} />
            <StatCard icon={<AlertTriangle size={20} />} label="免状・手帳 期限注意" value={`${kpi.expiry}名`} sub={`${warnDays.license}日以内・期限切れ`} color="#b45309" bg="#fef3c7"
              active={statusFilter === 'expiry'} onClick={() => clickKpi('expiry')} />
            <StatCard icon={<NotebookPen size={20} />} label="配乗上の注意あり" value={`${kpi.noted}名`} color="#a21caf" bg="#fdf4ff"
              active={statusFilter === 'note'} onClick={() => clickKpi('note')} />
          </div>

          {/* コントロール */}
          <div className="card" style={{ padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
                <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="氏名・フリガナ・社員コードで検索"
                  style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <select style={selectStyle} value={vesselFilter} onChange={e => setVesselFilter(e.target.value)}>
                <option value="">すべての船舶</option>
                <optgroup label="現在配属中">
                  {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
                </optgroup>
                <optgroup label="乗船経験あり（過去含む）">
                  {vessels.map(v => <option key={`exp:${v.id}`} value={`exp:${v.id}`}>{v.vessel_name} の経験者</option>)}
                </optgroup>
                <option value="none">未配乗のみ</option>
              </select>
              <select style={selectStyle} value={licenseFilter} onChange={e => setLicenseFilter(e.target.value)}>
                <option value="">すべての免状</option>
                {licenseOptions.map(g => <option key={g} value={g}>{g}</option>)}
                <option value="none">免状未登録</option>
              </select>
              <select style={selectStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">すべての状態</option>
                <option value="on_board">乗船中</option>
                <option value="scheduled">乗船予定</option>
                <option value="ashore">陸上・休暇</option>
                <option value="long_boarding">長期乗船（{warnDays.boarding}日以上）</option>
                <option value="departing_soon">まもなく下船（14日以内）</option>
                <option value="expiry">免状・手帳 期限注意</option>
                <option value="note">配乗上の注意あり</option>
              </select>
              <select style={selectStyle} value={sortKey} onChange={e => setSortKey(e.target.value as any)}>
                <option value="name">氏名順</option>
                <option value="code">社員コード順</option>
                <option value="total">通算乗船が長い順</option>
                <option value="ashore">陸上日数が長い順（配乗候補）</option>
                <option value="boarding">乗船日数が長い順（交代候補）</option>
              </select>

              {/* 保存ビュー（共有フィルタ） */}
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowViewMenu(v => !v)}
                  style={{ ...selectStyle, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                  <Filter size={13} />保存ビュー{savedFilters.length > 0 ? `（${savedFilters.length}）` : ''} <ChevronDown size={12} />
                </button>
                {showViewMenu && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300, minWidth: 260, overflow: 'hidden' }}
                    onMouseLeave={() => setShowViewMenu(false)}>
                    <button onClick={() => { saveCurrentFilter(); setShowViewMenu(false); }}
                      style={{ width: '100%', padding: '10px 14px', border: 'none', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: '#0369a1' }}>
                      <Save size={13} />現在の条件を保存…
                    </button>
                    {savedFilters.length === 0 && (
                      <div style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>保存されたビューはありません</div>
                    )}
                    {savedFilters.map(f => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                        <button onClick={() => applySavedFilter(f)}
                          style={{ flex: 1, padding: '9px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                          {f.created_by_name && <div style={{ fontSize: 10.5, color: '#94a3b8' }}>作成: {f.created_by_name}</div>}
                        </button>
                        <button title="削除" onClick={() => deleteSavedFilter(f)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', padding: '0 12px' }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                          onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(ageBand || durBand) && (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  {ageBand && <Pill bg="#e0f2fe" fg="#0369a1" border="#bae6fd">年齢: {ageBand} <button onClick={() => setAgeBand('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0369a1', padding: 0, fontWeight: 800 }}>×</button></Pill>}
                  {durBand && <Pill bg="#e0f2fe" fg="#0369a1" border="#bae6fd">通算: {durBand} <button onClick={() => setDurBand('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0369a1', padding: 0, fontWeight: 800 }}>×</button></Pill>}
                </span>
              )}

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* グルーピング */}
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
                  {([['none', 'グループなし'], ['vessel', '船別'], ['license', '免状別'], ['position', '役職別']] as [GroupMode, string][]).map(([m, label]) => (
                    <button key={m} onClick={() => setGroupMode(m)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: groupMode === m ? '#fff' : 'transparent',
                        color: groupMode === m ? '#0369a1' : '#64748b',
                        boxShadow: groupMode === m ? '0 1px 4px rgba(15,23,42,0.12)' : 'none',
                      }}>{label}</button>
                  ))}
                </div>
                {/* 表示切替 */}
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
                  {([['card', <LayoutGrid key="g" size={14} />], ['list', <LayoutList key="l" size={14} />]] as [ViewMode, React.ReactNode][]).map(([m, icon]) => (
                    <button key={m} onClick={() => changeView(m)} title={m === 'card' ? 'カード表示' : 'リスト表示'}
                      style={{
                        padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex',
                        background: viewMode === m ? '#fff' : 'transparent',
                        color: viewMode === m ? '#0369a1' : '#64748b',
                        boxShadow: viewMode === m ? '0 1px 4px rgba(15,23,42,0.12)' : 'none',
                      }}>{icon}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 本体 */}
          {loading ? (
            <div className="card"><div className="empty" style={{ padding: 40 }}>読み込み中…</div></div>
          ) : filtered.length === 0 ? (
            <div className="card"><div className="empty" style={{ padding: 40 }}>該当する乗組員がいません</div></div>
          ) : (
            <>
              {usePaging && pager.renderBar('top')}
              {displayGroups.map(g => (
                <section key={g.key} style={{ marginBottom: 20 }}>
                  {g.label && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px' }}>
                      {groupMode === 'vessel' && <Ship size={15} color="#0369a1" />}
                      {groupMode === 'license' && <Award size={15} color="#a21caf" />}
                      {groupMode === 'position' && <IdCard size={15} color="#0369a1" />}
                      <span style={{ fontSize: 14.5, fontWeight: 800, color: '#0f172a' }}>{g.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', background: '#f1f5f9', borderRadius: 999, padding: '1px 10px' }}>{g.rows.length}名</span>
                      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                    </div>
                  )}
                  {viewMode === 'card' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                      {g.rows.map(r => <PersonCard key={r.id} row={r} onClick={() => setSelectedId(r.id)} />)}
                    </div>
                  ) : renderTable(g.rows)}
                </section>
              ))}
              {usePaging && pager.renderBar('bottom')}
            </>
          )}
        </>
      )}

      {/* ═══ タブ2: 人員分析 ═══ */}
      {pageTab === 'analytics' && (
        <>
          {/* 分析KPI */}
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
            <StatCard icon={<Users size={20} />} label="平均年齢" value={analytics.avgAge != null ? `${analytics.avgAge}歳` : '-'} color="#0369a1" bg="#e0f2fe" />
            <StatCard icon={<History size={20} />} label="平均通算乗船" value={fmtDuration(analytics.avgTotal)} sub={`${analytics.avgTotal.toLocaleString()}日`} color="#0369a1" bg="#e0f2fe" />
            <StatCard icon={<Award size={20} />} label="免状保有率" value={rows.length ? `${Math.round(analytics.licenseHolders / rows.length * 100)}%` : '-'} sub={`${analytics.licenseHolders}/${rows.length}名`} color="#a21caf" bg="#fdf4ff" />
            <StatCard icon={<Ship size={20} />} label="乗船中率" value={rows.length ? `${Math.round(kpi.onBoard / rows.length * 100)}%` : '-'} sub={`${kpi.onBoard}/${rows.length}名`} color="#16a34a" bg="#dcfce7" />
            <StatCard icon={<Anchor size={20} />} label="平均陸上日数" value={analytics.avgAshore != null ? `${analytics.avgAshore}日` : '-'} sub="陸上者のみ" color="#64748b" bg="#f1f5f9" />
          </div>

          <div style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>
            💡 グラフのバーをクリックすると、該当する乗組員を名簿で絞り込み表示します
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16 }}>
            {/* 年齢構成 */}
            <ChartCard title="年齢構成" hint="内航船員の高齢化対策の基礎データ">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.ageDist} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  {chartTooltip}
                  <Bar dataKey="count" radius={[5, 5, 0, 0]} maxBarSize={48} cursor="pointer"
                    onClick={(d: any) => { if (d?.name) drillTo(() => setAgeBand(d.name)); }}>
                    {analytics.ageDist.map((d, i) => (
                      <Cell key={i} fill={d.name === ageBand ? CHART_DEEP : d.count === 0 ? '#E5E7EB' : CHART_BLUE} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 免状構成 */}
            <ChartCard title="免状種別の保有人数" hint="クリックで該当者を表示">
              <ResponsiveContainer width="100%" height={Math.max(220, analytics.licDist.length * 34)}>
                <BarChart data={analytics.licDist} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} />
                  {chartTooltip}
                  <Bar dataKey="count" radius={[0, 5, 5, 0]} maxBarSize={20} cursor="pointer"
                    onClick={(d: any) => { if (d?.name) drillTo(() => setLicenseFilter(d.name === '免状未登録' ? 'none' : d.name)); }}>
                    {analytics.licDist.map((d, i) => (
                      <Cell key={i} fill={d.name === '免状未登録' ? '#CBD5E1' : '#a855f7'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 通算乗船日数の分布 */}
            <ChartCard title="通算乗船日数の分布" hint="経験値の層の厚み（手動履歴含む）">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.durDist} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  {chartTooltip}
                  <Bar dataKey="count" radius={[5, 5, 0, 0]} maxBarSize={48} cursor="pointer"
                    onClick={(d: any) => { if (d?.name) drillTo(() => setDurBand(d.name)); }}>
                    {analytics.durDist.map((d, i) => (
                      <Cell key={i} fill={d.name === durBand ? CHART_DEEP : d.count === 0 ? '#E5E7EB' : '#10b981'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 船別の乗船人数 */}
            <ChartCard title="船別の乗船人数" hint="現在の配乗バランス">
              <ResponsiveContainer width="100%" height={Math.max(220, analytics.vesselDist.length * 34)}>
                <BarChart data={analytics.vesselDist} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} />
                  {chartTooltip}
                  <Bar dataKey="count" radius={[0, 5, 5, 0]} maxBarSize={20} cursor="pointer"
                    onClick={(d: any) => { if (d?.id) drillTo(() => setVesselFilter(d.id)); }}>
                    {analytics.vesselDist.map((_, i) => <Cell key={i} fill={CHART_BLUE} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {analytics.vesselDist.length === 0 && <div className="empty" style={{ padding: 20 }}>乗船中の乗組員がいません</div>}
            </ChartCard>
          </div>
        </>
      )}

      {/* ═══ タブ3: 免状・手帳期限 ═══ */}
      {pageTab === 'expiry' && (
        <>
          {hasFeature('crew_certificates') && (
            <div className="card" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, background: '#f5f3ff', border: '1.5px solid #ddd6fe' }}>
              <Award size={18} color="#7c3aed" />
              <div style={{ flex: 1, fontSize: 13, color: '#4c1d95' }}>
                <b>資格・期限管理</b> ページでは、STCW基本訓練・健康証明書も含めた全種別を船別に一覧し、更新登録やExcel出力（各人有効期限／船別有効期限表）ができます。
              </div>
              <button className="btn-p" style={{ background: '#7c3aed' }} onClick={() => navigate('/crew-certificates')}>開く</button>
            </div>
          )}
          <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>対象期間:</span>
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
              {[30, 60, 90, 180].map(d => (
                <button key={d} onClick={() => setExpiryDays(d)}
                  style={{
                    padding: '5px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    background: expiryDays === d ? '#fff' : 'transparent',
                    color: expiryDays === d ? '#b45309' : '#64748b',
                    boxShadow: expiryDays === d ? '0 1px 4px rgba(15,23,42,0.12)' : 'none',
                  }}>{d}日以内</button>
              ))}
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>期限切れは常に表示されます</span>
            <button className="btn-o" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}
              onClick={() => exportExpiry().catch(console.error)}>
              <Download size={13} />このリストをExcel出力
            </button>
          </div>

          {expiryList.length === 0 ? (
            <div className="card">
              <div className="empty" style={{ padding: 40 }}>
                ✅ {expiryDays}日以内に失効する免状・船員手帳はありません
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'auto' }}>
              <table>
                <thead><tr>
                  <th>氏名</th><th>区分</th><th>免状種別 / 手帳番号</th><th>有効期限</th><th>残日数</th><th>現在の配属</th><th>連絡先</th>
                </tr></thead>
                <tbody>
                  {expiryList.map((e, i) => (
                    <tr key={i} onClick={() => setSelectedId(e.user.id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div onClick={ev => ev.stopPropagation()}>
                            <AvatarEditor entityId={e.user.id} type="user" imagePath={e.user.avatar_path} displayName={e.user.user_name} size={34} readOnly onUpdated={() => {}} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{e.user.user_name}</div>
                            <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{e.user.employee_code}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Pill bg={e.kind === '海技免状' ? '#fdf4ff' : '#eff6ff'} fg={e.kind === '海技免状' ? '#a21caf' : '#2563eb'}>
                          {e.kind}
                        </Pill>
                      </td>
                      <td style={{ fontSize: 12.5 }}>{e.grade}</td>
                      <td style={{ fontSize: 12.5, fontWeight: 700 }}>{fmtDate(e.date)}</td>
                      <td>
                        {e.days < 0
                          ? <Pill bg="#fef2f2" fg="#dc2626" border="#fecaca"><AlertTriangle size={11} />期限切れ（{Math.abs(e.days)}日超過）</Pill>
                          : <Pill bg={e.days <= 30 ? '#fef2f2' : '#fffbeb'} fg={e.days <= 30 ? '#dc2626' : '#b45309'} border={e.days <= 30 ? '#fecaca' : '#fde68a'}>残{e.days}日</Pill>}
                      </td>
                      <td style={{ fontSize: 12.5 }}>
                        {e.user.current
                          ? <><Ship size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{e.user.current.vessel_name}</>
                          : <span style={{ color: '#94a3b8' }}>陸上</span>}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{e.user.email || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10 }}>
            💡 行をクリックすると個人詳細を表示します。更新手続き後の期限変更は「ユーザー管理 → 編集」から行えます。
          </div>
        </>
      )}

      {/* 詳細ドロワー（共通コンポーネント） */}
      {selectedId && (
        <TalentDetailDrawer userId={selectedId} onClose={() => setSelectedId(null)} onChanged={() => loadRoster(true)} />
      )}
    </div>
  );
};

export default TalentPage;
